// src/components/Stores/GetAdminStores.jsx
import React, { useEffect, useState } from "react";
import {
    Box,
    Typography,
    Button,
    CircularProgress,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    TableContainer,
    Paper,
    Chip,
    IconButton,
    Menu,
    MenuItem,
    ListItemIcon,
    ListItemText,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    Snackbar,
    Alert,
    TextField,
    FormControl,
    InputLabel,
    Select,
    Pagination,
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import StoreIcon from "@mui/icons-material/Store";
import { useNavigate } from "react-router-dom";
import { getAllStoresAdmin, deleteStore } from "../../Apis/StoreAPI";

const GetAdminStores = () => {
    const [stores, setStores] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [anchorEl, setAnchorEl] = useState(null);
    const [selectedStore, setSelectedStore] = useState(null);
    const [openConfirm, setOpenConfirm] = useState(false);
    const [storeToDelete, setStoreToDelete] = useState(null);
    const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

    const [searchQuery, setSearchQuery] = useState("");
    const [statusFilter, setStatusFilter] = useState("");
    const [page, setPage] = useState(1); // 
    const [limit, setLimit] = useState(10);
    const [totalPages, setTotalPages] = useState(1);
    const [totalItems, setTotalItems] = useState(0);

    const navigate = useNavigate();

    // Fetch stores
    const fetchStores = async () => {
        try {
            setLoading(true);
            const params = {
                search: searchQuery.trim(),
                page, // 
                limit,
                status: statusFilter || undefined,
            };

            const response = await getAllStoresAdmin(params);
            setStores(response.data || []);
            setTotalPages(response.totalPages || 1);
            setTotalItems(response.totalItems || response.data?.length || 0);
        } catch (err) {
            setError("Failed to fetch stores.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchStores();
    }, [page, limit]);

    const handleSearch = () => {
        setPage(1);
        fetchStores();
    };

    const handleMenuOpen = (event, store) => {
        setAnchorEl(event.currentTarget);
        setSelectedStore(store);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
        setSelectedStore(null);
    };

    const handleEdit = (store) => {
        navigate(`/editstore/${store.id || store._id}`, { state: { store } });
        handleMenuClose();
    };
    const handleStoreCounters = (store) => {
        navigate("/storecounter/list", { state: { storeId: store.id } });
        handleMenuClose();
    };
    console.log(stores, "stores")

    const handleDeleteClick = (store) => {
        setStoreToDelete(store);
        setOpenConfirm(true);
        handleMenuClose();
    };

    const handleDelete = async () => {
        try {
            await deleteStore(storeToDelete.id || storeToDelete._id);
            setStores((prev) =>
                prev.filter((s) => (s.id || s._id) !== (storeToDelete.id || storeToDelete._id))
            );
            setSnackbar({ open: true, message: "Store deleted successfully!", severity: "success" });
            fetchStores();
        } catch (err) {
            setSnackbar({ open: true, message: "Failed to delete store.", severity: "error" });
        } finally {
            setOpenConfirm(false);
            setStoreToDelete(null);
        }
    };

    const normalizeStatus = (val) => val === true || val === "true" || val === 1 || val === "1";
    const getStatusLabel = (status) => (normalizeStatus(status) ? "Active" : "Inactive");

    return (
        <Box sx={{ padding: 3, width: "95%", margin: "auto" }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
                <Typography variant="h4" fontWeight="bold">Stores</Typography>
                <Button variant="contained" onClick={() => navigate("/getadminstores/add")}>
                    + Add Store
                </Button>
            </Box>

            {/* Search and Filter */}
            <Box sx={{ mb: 3, display: "flex", gap: 2 }}>
                <TextField
                    label="Search by name"
                    variant="outlined"
                    size="small"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    sx={{ width: 300 }}
                />
                <FormControl size="small" sx={{ width: 150 }}>
                    <InputLabel>Status</InputLabel>
                    <Select
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                        label="Status"
                    >
                        <MenuItem value="">All</MenuItem>
                        <MenuItem value="true">Active</MenuItem>
                        <MenuItem value="false">Inactive</MenuItem>
                    </Select>
                </FormControl>
                <Button variant="contained" onClick={handleSearch}>Search</Button>
            </Box>

            {loading && (
                <Box display="flex" justifyContent="center" alignItems="center" height={200}>
                    <CircularProgress />
                </Box>
            )}
            {error && <Alert severity="error">{error}</Alert>}

            {!loading && !error && (
                <Paper>
                    <TableContainer>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>S.No</TableCell>
                                    <TableCell>Image</TableCell>
                                    <TableCell>Name</TableCell>
                                    <TableCell>City</TableCell>
                                    <TableCell>Pincode</TableCell>
                                    <TableCell>Status</TableCell>
                                    <TableCell>Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {stores.map((store, index) => (
                                    <TableRow key={store.id || store._id}>
                                        <TableCell>{index + 1 + (page - 1) * limit}</TableCell>
                                        <TableCell>
                                            {store.image ? (
                                                <img
                                                    src={store.image}
                                                    alt={store.name}
                                                    width={70}
                                                    height={70}
                                                    style={{ borderRadius: 8, objectFit: "cover" }}
                                                />
                                            ) : (
                                                <StoreIcon fontSize="large" color="disabled" />
                                            )}
                                        </TableCell>
                                        <TableCell>{store.name}</TableCell>
                                        <TableCell>{store.city}</TableCell>
                                        <TableCell>{store.pincode}</TableCell>
                                        <TableCell>
                                            <Chip
                                                label={getStatusLabel(store.status)}
                                                color={normalizeStatus(store.status) ? "success" : "error"}
                                                size="small"
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <IconButton onClick={(e) => handleMenuOpen(e, store)}>
                                                <MoreVertIcon />
                                            </IconButton>
                                            <Menu
                                                anchorEl={anchorEl}
                                                open={Boolean(anchorEl) && ((selectedStore?.id || selectedStore?._id) === (store.id || store._id))}
                                                onClose={handleMenuClose}
                                            >
                                                <MenuItem onClick={() => handleEdit(store)}>
                                                    <ListItemIcon>
                                                        <EditIcon fontSize="small" />
                                                    </ListItemIcon>
                                                    <ListItemText>Edit</ListItemText>
                                                </MenuItem>
                                                <MenuItem onClick={() => handleStoreCounters(store)}>
                                                    <ListItemIcon>
                                                        <StoreIcon fontSize="small" />
                                                    </ListItemIcon>
                                                    <ListItemText>Store Counters</ListItemText>
                                                </MenuItem>
                                                <MenuItem onClick={() => handleDeleteClick(store)}>
                                                    <ListItemIcon>
                                                        <DeleteIcon fontSize="small" color="error" />
                                                    </ListItemIcon>
                                                    <ListItemText primaryTypographyProps={{ color: "error" }}>
                                                        Delete
                                                    </ListItemText>
                                                </MenuItem>
                                            </Menu>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Paper>
            )}
            {/* ✅ Pagination Controls */}
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: 2 }}>
                {/* Rows per page selector */}
                <FormControl size="small" sx={{ minWidth: 120 }}>
                    <Select
                        value={limit}
                        onChange={(e) => {
                            setLimit(parseInt(e.target.value, 10));
                            setPage(1); // reset page when rows per page changes
                        }}
                    >
                        {[5, 10, 20, 50].map((num) => (
                            <MenuItem key={num} value={num}>
                                {num} Rows
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>

                {/* Pagination */}
                <Pagination
                    count={totalPages}
                    page={page}
                    onChange={(e, val) => setPage(val)}
                    color="primary"
                />
            </Box>

            {/* Delete Confirmation Dialog */}
            <Dialog open={openConfirm} onClose={() => setOpenConfirm(false)}>
                <DialogTitle>Confirm Delete</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Are you sure you want to delete this store? This action cannot be undone.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenConfirm(false)}>Cancel</Button>
                    <Button onClick={handleDelete} color="error" variant="contained">Delete</Button>
                </DialogActions>
            </Dialog>

            {/* Snackbar */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={3000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                anchorOrigin={{ vertical: "top", horizontal: "right" }}
            >
                <Alert
                    onClose={() => setSnackbar({ ...snackbar, open: false })}
                    severity={snackbar.severity}
                    variant="filled"
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default GetAdminStores;
